<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';

	import { sub_units } from 'src/stores/account/home';

	import {
		ensureSubUnitsLoaded,
		getCurrentSubUnitId,
		getCurrentUserDisplayName,
		getHomeOwnerEmail,
		isSubUnitSession,
		labelForSubUnitId,
		summariseSceneNotificationRecipients,
	} from 'src/components/Scenes/helpers/notification_recipients_helpers';
	import type { ChannelRecipientSummary } from 'src/types/NotificationRecipients';

	import { Label, Text } from '@Shelly/shelly-design-system';
	import type { ISceneObj } from 'src/types/SceneType';

	export let scene: ISceneObj | undefined;

	/**
	 * FE-1682: hydrate the sub-unit roster on mount so recipient names
	 * resolve on the first page-load (no `Sub-unit #<id>` fallback) and so
	 * a sub-unit added from another screen / tab shows up here without a
	 * hard refresh. The helper is stale-while-revalidate, so any cached
	 * entries stay visible while the refresh request is in flight.
	 */
	onMount(() => {
		void ensureSubUnitsLoaded();
	});

	$: summary = (() => {
		// Touch the sub_units store so the "no longer in home" hint stays fresh
		// when the home roster changes while the side-panel is open.
		void $sub_units;
		return summariseSceneNotificationRecipients(scene);
	})();

	$: currentSubId = getCurrentSubUnitId();
	$: viewerIsOwner = !isSubUnitSession();
	$: ownerDisplayName = getCurrentUserDisplayName();
	$: ownerEmail = getHomeOwnerEmail();

	$: hasAnyChannel = !!summary.push || !!summary.mail;
	$: hideChannelTitles = !!summary.push && !!summary.mail;

	const channelTitle = (channel: ChannelRecipientSummary): string => {
		if (channel.notificationType === 'push_notification') {
			return $format('scene_notification_recipient_section_push', {
				default: 'Phone notifications',
			});
		}
		return $format('scene_notification_recipient_section_mail', {
			default: 'Email notifications',
		});
	};

	const channelIcon = (channel: ChannelRecipientSummary): string =>
		channel.notificationType === 'push_notification'
			? 'fas fa-mobile-signal-out'
			: 'fas fa-envelope';

	/**
	 * Owner badge text shown in the side-panel preview.
	 *
	 * - Owner session → "{name} (me)" so the viewer sees their own display
	 *   name with the same "(me)" suffix as the wizard.
	 * - Sub-unit session → the owner's email, since the row refers to someone
	 *   else and we don't know the owner's profile name from a sub-unit session.
	 */
	const ownerLabel = (): string =>
		viewerIsOwner
			? $format('scene_notification_recipient_owner_me_label', {
					default: '{name} (me)',
					values: { name: ownerDisplayName || ownerEmail },
				})
			: ownerEmail ||
				$format('scene_notification_recipient_owner_hint', {
					default: 'Home owner',
				});

	const subUnitLabel = (subId: number, name: string): string =>
		currentSubId !== undefined && subId === currentSubId
			? $format('scene_notification_recipient_current_user_label', {
					default: '{name} (you)',
					values: { name },
				})
			: name;
</script>

{#if hasAnyChannel}
	<div class="recipients-grid" class:two-cols={hideChannelTitles}>
		{#each [summary.push, summary.mail].filter(Boolean) as channel (channel.channel)}
			{@const hasExplicitRecipients =
				channel.subUnits.length > 0 || channel.orphanSubIds.length > 0}
			{@const isPureLegacy = channel.legacyAllMembers && !hasExplicitRecipients}
			<div class="channel-cell">
				<div class="channel-header">
					<i class={channelIcon(channel)} aria-hidden="true"></i>
					<Text styleType="label-large">{channelTitle(channel)}</Text>
				</div>

				{#if isPureLegacy}
					<Text styleType="body-small" class="text-color-muted">
						{$format('scene_notification_recipient_section_all_members', {
							default: 'Sent to every member of this home.',
						})}
					</Text>
				{:else if !channel.ownerIncluded && !hasExplicitRecipients}
					<Text styleType="body-small" class="text-color-muted">
						{$format('scene_notification_recipient_section_none', {
							default: 'No recipients selected — nobody will be notified.',
						})}
					</Text>
				{:else}
					<div class="recipient-list">
						{#if channel.ownerIncluded}
							<Label
								class="justify-content-start no-capitalize recipient-label"
								icon={viewerIsOwner ? 'fas fa-bell' : 'fas fa-house'}
								value={ownerLabel()}
								size="s"
							/>
						{/if}
						{#each channel.subUnits as recipient (recipient.subId)}
							<Label
								class="justify-content-start no-capitalize recipient-label"
								icon={recipient.isCurrentUser ? 'fas fa-bell' : 'fas fa-user'}
								value={subUnitLabel(recipient.subId, recipient.name || recipient.email)}
								size="s"
							/>
						{/each}
						{#each channel.orphanSubIds as orphan (orphan)}
							<Label
								class="justify-content-start no-capitalize recipient-label"
								icon="fas fa-user-slash"
								value={labelForSubUnitId(orphan)}
								size="s"
							/>
						{/each}
					</div>

					{#if channel.legacyAllMembers}
						<Text styleType="body-small" class="text-color-muted">
							{$format('scene_notification_recipient_section_legacy_mixed', {
								default:
									'Some actions still use the legacy "all members" delivery and will reach every member of this home.',
							})}
						</Text>
					{/if}
				{/if}
			</div>
		{/each}
	</div>
{/if}

<style>
	:global(.no-capitalize .label-value)::first-letter {
		text-transform: none !important;
	}

	.recipients-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: var(--spacing-m, 16px);
		align-items: start;
		width: 100%;
	}

	.recipients-grid.two-cols {
		grid-template-columns: 1fr 1fr;
		column-gap: var(--spacing-s, 16px);
	}

	.channel-cell {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s, 8px);
		min-width: 0;
	}

	.channel-header {
		display: flex;
		align-items: center;
		gap: var(--spacing-s, 8px);
	}

	.recipient-list {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--spacing-xs, 4px);
		min-width: 0;
	}
	.recipient-list :global(.recipient-label) {
		align-self: stretch;
		max-width: 100%;
		min-width: 0;
		height: auto;
		min-height: 0;
	}

	.recipient-list :global(.recipient-label .label-value-container) {
		min-width: 0;
		flex: 1 1 auto;
		text-align: left;
	}

	.recipient-list :global(.recipient-label .label-value) {
		display: block;
		min-width: 0;
		max-width: 100%;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
</style>
